import React, { useCallback, useState } from "react"
import { useSelector } from "react-redux"
import { Navigate } from "react-router-dom"
import { Formik } from "formik"
import ReCAPTCHA from "react-google-recaptcha"
import { useActions } from "store/utils"
import { sendPIN, sendSMS } from "store/app/actions"
import { updateProfile } from "store/profile/actions"
import ProfileFetcher from "./ProfileFetcher"
import Form from "./Form"
import FormInput from "components/FormInput"
import { Submit } from "./Submit"
import "./LoginForm.sass"

const initialValues = {
  phoneNumber: "",
  code: "",
}

const initialProfileValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
}

const digitRegex = /^[0-9]*$/

function validatePhoneForm(values) {
  const errors = {}
  if (!values.phoneNumber || values.phoneNumber.length < 9) {
    errors.phoneNumber = "Vnesite telefonsko številko."
  }

  if (!digitRegex.test(values.phoneNumber)) {
    errors.phoneNumber = "Številka vsebuje nedovoljene znake."
  }

  return errors
}

function validateCodeForm(values) {
  const errors = {}
  if (!values.code || values.code.length < 5) {
    errors.code = "Vnesite kodo."
  }

  if (!digitRegex.test(values.code)) {
    errors.phoneNumber = "Koda naj vsebuje samo številke."
  }

  return errors
}

function SMSStep({ isLoading }) {
  const actions = useActions({
    sendSMS: (phone, captcha) => sendSMS(phone, captcha),
  })
  const [captcha, setCaptcha] = useState(undefined)
  const [captchaError, setCaptchaError] = useState(undefined)

  const handlePhoneSubmission = useCallback(
    async (values) => {
      if (captcha === undefined) {
        setCaptchaError("Captcha ni izpolnjena.")
        return
      }
      setCaptchaError(undefined)
      initialValues.phoneNumber = values.phoneNumber
      initialProfileValues.phone = values.phoneNumber
      await actions.sendSMS(values.phoneNumber, captcha)
    },
    [actions, captcha],
  )

  return (
    <>
      <div className="Steps">
        <span className="active">
          <span className="number">1</span> <span className="label">SMS</span>
        </span>
        <span>
          <span className="number">2</span> <span className="label">PIN</span>
        </span>
      </div>
      <Formik
        initialValues={initialValues}
        validate={validatePhoneForm}
        onSubmit={handlePhoneSubmission}
      >
        {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            // isSubmitting,
          }) => (
          <Form className="LoginForm" onSubmit={handleSubmit}>
            <fieldset
              className={
                touched.phoneNumber && errors.phoneNumber ? "error" : undefined
              }
            >
              <FormInput
                autoFocus
                placeholder="Telefonska številka"
                value={values.phoneNumber}
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                onChange={handleChange}
                onBlur={handleBlur}
                tabIndex={1}
                disabled={isLoading ? "disabled" : undefined}
                style={{
                  width: '304px',
                  marginBottom: '16px',
                }}
              />
              <ReCAPTCHA
                sitekey={"6Ld5SmMqAAAAABrICNf34a1JLVA7H-ntBxJ_OJau"}
                onChange={(val) => setCaptcha(val)}
              />
              <div className="errors">
                {touched.phoneNumber && errors.phoneNumber}
              </div>
              {captchaError && <div className="errors">
                {captchaError}
              </div>}
            </fieldset>

            <Submit tabIndex={2} onClick={handleSubmit} isDisabled={isLoading}>
              Pošlji SMS
            </Submit>

            <input
              type="submit"
              value="Pošlji SMS"
              style={{ display: "none" }}
            />
          </Form>
        )}
      </Formik>
    </>
  )
}

function PINStep({ isLoading }) {
  const actions = useActions({
    sendPIN: (phone, pin) => sendPIN(phone, pin),
  })

  const handleCodeSubmission = useCallback(
    async (values) => {
      await actions.sendPIN(values.phoneNumber, values.code)
    },
    [actions],
  )

  return (
    <>
      <div className="Steps">
        <span className="done">
          <span className="number">1</span> <span className="label">SMS</span>
        </span>
        <span className="active">
          <span className="number">2</span> <span className="label">PIN</span>
        </span>
      </div>

      <Formik
        initialValues={initialValues}
        validate={validateCodeForm}
        onSubmit={handleCodeSubmission}
      >
        {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
          <Form className="LoginForm" onSubmit={handleSubmit}>
            <fieldset
              className={touched.code && errors.code ? "error" : undefined}
            >
              <FormInput
                autoFocus
                placeholder="Verifikacijski PIN"
                tabIndex={3}
                value={values.code}
                type="text"
                pattern="[0-9]{5}"
                id="code"
                name="code"
                onChange={handleChange}
                onBlur={handleBlur}
                // ref={codeRef}
                maxLength={5}
                disabled={isLoading ? "disabled" : undefined}
              />

              <div className="errors">{touched.code && errors.code}</div>
            </fieldset>

            <Submit onClick={handleSubmit} tabIndex={4} isDisabled={isLoading}>
              Prijavi me
            </Submit>

            <input
              type="submit"
              value="Prijavi me"
              style={{ display: "none" }}
            />
          </Form>
        )}
      </Formik>
    </>
  )
}

// eslint-disable-next-line no-useless-escape
const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

function validateProfileForm(values) {
  const errors = {}
  if (!values.first_name || values.first_name.trim() === "") {
    errors.first_name = "Vnesite ime."
  }
  if (!values.last_name || values.last_name.trim() === "") {
    errors.last_name = "Vnesite priimek."
  }
  if (!values.email || !emailRegex.test(values.email.toLowerCase())) {
    errors.email = "Vnesite email naslov."
  }

  return errors
}

function ProfileStep() {
  const actions = useActions({
    updateProfile: (first_name, last_name, email) =>
      updateProfile(first_name, last_name, email),
  })

  const handleProfileSubmission = useCallback(
    async (values) => {
      await actions.updateProfile(
        values.first_name,
        values.last_name,
        values.email,
      )
    },
    [actions],
  )

  const { isLoading } = useSelector((state) => state.profile)

  return (
    <>
      <div className="Steps">
        <span className="done">
          <span className="number">1</span> <span className="label">SMS</span>
        </span>
        <span className="done">
          <span className="number">2</span> <span className="label">PIN</span>
        </span>
        <span className="active">
          <span className="number">3</span>{" "}
          <span className="label">Profil</span>
        </span>
      </div>

      <p>
        Dobrodošli v Avtologu! Pred prvim vstopom se nam na kratko predstavite.
      </p>

      <Formik
        initialValues={initialProfileValues}
        validate={validateProfileForm}
        onSubmit={handleProfileSubmission}
      >
        {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
          <Form className="LoginForm Profile" onSubmit={handleSubmit}>
            <div className="name">
              <fieldset
                className={
                  touched.first_name && errors.first_name ? "error" : undefined
                }
              >
                <FormInput
                  autoFocus
                  placeholder="Ime"
                  tabIndex={3}
                  value={values.first_name}
                  type="text"
                  id="first_name"
                  name="first_name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading ? "disabled" : undefined}
                />

                <div className="errors">
                  {touched.first_name && errors.first_name}
                </div>
              </fieldset>

              <fieldset
                className={
                  touched.last_name && errors.last_name ? "error" : undefined
                }
              >
                <FormInput
                  placeholder="Priimek"
                  tabIndex={3}
                  value={values.last_name}
                  type="text"
                  id="last_name"
                  name="last_name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading ? "disabled" : undefined}
                />

                <div className="errors">
                  {touched.last_name && errors.last_name}
                </div>
              </fieldset>
            </div>

            <div className="email">
              <fieldset
                className={touched.email && errors.email ? "error" : undefined}
              >
                <FormInput
                  placeholder="Email naslov"
                  tabIndex={3}
                  value={values.email}
                  type="email"
                  id="email"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading ? "disabled" : undefined}
                />

                <div className="errors">{touched.email && errors.email}</div>
              </fieldset>

              <Submit
                onClick={handleSubmit}
                tabIndex={4}
                isDisabled={isLoading}
              >
                Dokončaj prijavo
              </Submit>

              <input
                type="submit"
                value="Dokončaj prijavo"
                style={{ display: "none" }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

function Discretion() {
  return (
    <p className="discretion">
      Prijava v Avtolog je brezplačna. Vaših podatkov ne bomo delili s tretjimi
      osebami.
    </p>
  )
}

export default function LoginForm({ redirect = "/moja-garaza/" }) {
  const { authenticated, smsSuccess, isLoading } = useSelector(
    (state) => state.app,
  )

  const { entry: profile, isLoaded: isProfileLoaded } = useSelector(
    (state) => state.profile,
  )

  if (!smsSuccess) {
    return (
      <>
        <SMSStep isLoading={isLoading} />
        <Discretion />
      </>
    )
  }

  if (!authenticated) {
    return (
      <>
        <PINStep isLoading={isLoading} />
        <Discretion />
      </>
    )
  }

  if (!!profile.phone && !profile.email) {
    return (
      <>
        <ProfileFetcher />
        {isProfileLoaded ? (
          <ProfileStep />
        ) : (
          <div className="Steps">
            <span className="done">
              <span className="number">1</span>{" "}
              <span className="label">SMS</span>
            </span>
            <span className="done">
              <span className="number">2</span>{" "}
              <span className="label">PIN</span>
            </span>
          </div>
        )}
      </>
    )
  }

  return <Navigate to={redirect} />
}
